import React, { useEffect, useState, useMemo, useCallback } from 'react';
import _ from 'lodash';

import dayjs from 'dayjs';
import { useLoadingBackdrop } from 'hooks/useLoadingBackdrop';
import { Button, Link, Modal, Typography } from 'ui-kit';
import Table from 'components/Table';

import { exportPayoutDetails, useOwnerPayoutDetails } from 'api/owner_sdk';
import { Owner, PaymentPlan } from 'api/types/owner';
import { BalanceSign } from 'api/types/balances';
import { formatCurrency } from 'utils';
import { downloadListOfListsAsCSV } from 'utils/csv';

interface IOrderProductDetailsModal {
  owner: Owner;
  storeId: number;
  payoutStartDate: Date;
  payoutEndDate: Date;
  handleClose: () => void;
}

const OrderProductDetailsModal = ({
  owner,
  storeId,
  payoutStartDate,
  payoutEndDate,
  handleClose,
}: IOrderProductDetailsModal) => {
  const [totalOwnerProfit, setTotalOwnerProfit] = useState('N/A');
  const [showLoading, hideLoading] = useLoadingBackdrop();

  const { data: payoutObject, isLoading } = useOwnerPayoutDetails({
    storeId,
    payoutStartDate,
    payoutEndDate,
  });

  useEffect(() => {
    if (isLoading) {
      showLoading();
    } else {
      hideLoading();
    }
  }, [isLoading]);

  useEffect(() => {
    if (payoutObject && payoutObject.order_products.length > 0) {
      const ownerTotalProfit = payoutObject.order_products
        .map((order_product) => order_product.owner_profit)
        .reduce((prev, next) => prev + next);

      setTotalOwnerProfit(formatCurrency(ownerTotalProfit, payoutObject.currency));
    }
  }, [payoutObject]);

  const modalTitle = useMemo(
    () => `Payout details for store ${payoutObject?.store.store_name}`,
    [payoutObject],
  );

  const showModal = useMemo(() => !isLoading, [isLoading]);

  const handlePayoutDetailsExport = useCallback(
    () =>
      exportPayoutDetails({
        storeId,
        payoutStartDate,
        payoutEndDate,
      }).then((res) => {
        const dataForCSV = [
          [
            'Item',
            'Delivery date',
            'Item price',
            'Quantity',
            'Total (before fees and taxes)',
            'Refunded amount',
            'Merchant fees',
            'HostCo commission',
            'Your Profit',
          ],
          ...res.order_products.map((order_product) => [
            order_product.product_name,
            dayjs(order_product.delivery_date).format('DD MMMM YYYY'),
            order_product.price,
            order_product.quantity,
            order_product.amount,
            order_product.amount_refunded,
            order_product.transaction_fee,
            order_product.hostco_profit,
            order_product.owner_profit,
          ]),
        ];

        downloadListOfListsAsCSV({
          data: dataForCSV,
          filename: `${res.store.store_name}_payout_details.csv`,
        });
      }),
    [owner, storeId, payoutStartDate, payoutEndDate],
  );

  return (
    <Modal
      className={null}
      width={'100%'}
      title={modalTitle}
      visible={showModal}
      footer={null}
      destroyOnClose
      onCancel={handleClose}
    >
      <Typography variant="h6">Purchased products</Typography>
      <Typography variant="caption" as="div">
        * Item Price does not include guest convenience fee. Totals may not be exact due to process
        timing.
      </Typography>
      {owner.plan === PaymentPlan.Basic ? (
        <Typography variant="caption" as="div">
          * <Link href={`https://stripe.com/en-bg/pricing`}>Merchant processing fees</Link> are
          average 2.9% + .30¢ per US transaction. <Link href={`/pricing/`}>Upgrade to Pro</Link> to
          have these fees covered by The Host Co.
        </Typography>
      ) : (
        <Typography variant="caption" as="div">
          * <Link href={`https://stripe.com/en-bg/pricing`}>Merchant processing fees</Link> are
          average 2.9% + .30¢ per US transaction.{' '}
          <strong>Merchant fees are covered by The Host Co for your current payment plan.</strong>
        </Typography>
      )}

      {payoutObject && !isLoading && (
        <>
          <Table
            className="DashboardPayouts_table"
            scroll={{ x: '100%' }}
            dataSource={payoutObject.order_products}
            columns={[
              {
                title: 'Item',
                dataIndex: 'product_name',
                key: 'product_mame',
                render: (_text, record) => <>{record.product_name}</>,
              },
              {
                title: 'Delivery date',
                dataIndex: 'delivery_date',
                key: 'delivery_date',
                render: (text, _record) => <>{dayjs(text).format('MMM D, YYYY')}</>,
              },
              {
                title: 'Item price',
                dataIndex: 'price',
                key: 'price',
                render: (_text, record) => (
                  <>{formatCurrency(record.price, payoutObject.currency)}</>
                ),
              },
              {
                title: 'Quantity',
                dataIndex: 'quantity',
                key: 'quantity',
              },

              {
                title: (
                  <div>
                    Total
                    <br />
                    <span style={{ fontSize: '12px', color: '#959097' }}>
                      (before fees and taxes)
                    </span>
                  </div>
                ),
                dataIndex: 'amount',
                key: 'amount',
                render: (_text, record) => (
                  <>{formatCurrency(record.amount, payoutObject.currency)}</>
                ),
              },
              {
                title: 'Refunded amount',
                dataIndex: 'order_product_amount_refunded',
                key: 'order_product_amount_refunded',
                render: (_text, record) => (
                  <>{formatCurrency(record.amount_refunded, payoutObject.currency)}</>
                ),
              },
              {
                title: 'Merchant fees',
                dataIndex: 'transaction_fee',
                key: 'transaction_fee',
                render: (_text, record) => (
                  <>{formatCurrency(record.transaction_fee, payoutObject.currency)}</>
                ),
              },

              {
                title: 'Host Co commission',
                dataIndex: 'hostco_profit',
                key: 'hostco_profit',
                render: (_text, record) => (
                  <>{formatCurrency(record.hostco_profit, payoutObject.currency)}</>
                ),
              },
              {
                title: (
                  <div>
                    Your profit
                    <br />
                    <span>(Total: {totalOwnerProfit})</span>
                  </div>
                ),
                dataIndex: 'owner_profit',
                key: 'owner_profit',
                render: (_text, record) => (
                  <>{formatCurrency(record.owner_profit, payoutObject.currency)}</>
                ),
              },
            ]}
          />
          <div className="DashboardPayouts_table_button_wrapper">
            <Button
              className="DashboardPayouts_table_button"
              variant="contained"
              onClick={handlePayoutDetailsExport}
            >
              Export
            </Button>
          </div>
        </>
      )}
      {payoutObject && !isLoading && !_.isEmpty(payoutObject.applied_balances) && (
        <>
          <Typography variant="h6">Additional transactions</Typography>
          <Typography variant="caption" as="div">
            * Balance amounts are already applied to your total profit from products in this store.
          </Typography>
          <Table
            className="DashboardPayouts_table"
            scroll={{ x: '100%' }}
            dataSource={payoutObject.applied_balances}
            columns={[
              {
                title: 'Note',
                dataIndex: 'external_note',
                key: 'external_note',
              },
              {
                title: 'Balance adjustment',
                dataIndex: 'amount',
                key: 'amount',
                render: (_text, record) => (
                  <>
                    {record.sign === BalanceSign.DECREASE && (
                      <div style={{ color: 'red' }}>
                        -{formatCurrency(record.amount, payoutObject.currency)}
                      </div>
                    )}
                    {record.sign === BalanceSign.INCREASE && (
                      <div style={{ color: 'green' }}>
                        {formatCurrency(record.amount, payoutObject.currency)}
                      </div>
                    )}
                  </>
                ),
              },
              {
                title: 'Date created',
                dataIndex: 'date_created',
                key: 'date_created',
                render: (text, _record) => <>{dayjs(text).format('MMM D, YYYY')}</>,
              },
            ]}
          />
        </>
      )}

      {payoutObject && !isLoading && !_.isEmpty(payoutObject.not_applied_balances) && (
        <>
          <Typography variant="h6">Additional transactions</Typography>
          <Typography variant="caption" as="div">
            * Balance amounts will be applied to your total profit from products in this store at
            the payout day.
          </Typography>
          <Table
            className="DashboardPayouts_table"
            scroll={{ x: '100%' }}
            dataSource={payoutObject.not_applied_balances}
            columns={[
              {
                title: 'Note',
                dataIndex: 'external_note',
                key: 'external_note',
              },
              {
                title: 'Balance adjustment',
                dataIndex: 'leftover_amount',
                key: 'leftover_amount',
                render: (_text, record) => (
                  <>
                    {record.sign === BalanceSign.DECREASE && (
                      <div style={{ color: 'red' }}>
                        -{formatCurrency(record.leftover_amount, payoutObject.currency)}
                      </div>
                    )}
                    {record.sign === BalanceSign.INCREASE && (
                      <div style={{ color: 'green' }}>
                        {formatCurrency(record.leftover_amount, payoutObject.currency)}
                      </div>
                    )}
                  </>
                ),
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
};

export default OrderProductDetailsModal;
